import React, { useRef } from "react";
import { Navigate, useOutletContext } from "react-router-dom";

import { W0, W1 } from "@components/Constants";
import { useAppContext } from "@components/AppContext";
import { type AppContext } from "@components/AppContext/types";
import { OrganizationIntegration } from "@modules/backstage/organizations";
import { OrganizationUpdateModal } from "@components/Organization";
import isIntegrationEnabled from "@layouts/backstage/BackstageSettingsLayout/helpers/isIntegrationEnabled";

const LABEL_UPDATE_LINK_TEMPLATES_TITLE = "Edit Custom Links";
const PATH_SETTINGS_ORGANIZATION = "/backstage/settings/organization";
const DESCRIPTION_LINK_TEMPLATES = "Investor notification emails include deep links back to the portal. When using an external investor portal on your own domain, configure these URLs so notifications point to your frontend instead of the default Fundamental portal.";

type LinkTemplates = {
  documentsUrl?: string;
  investmentSignThankYouUrl?: string;
  investmentSignUrl?: string;
  investmentUrl?: string;
  portalUrl?: string;
  signInUrl?: string;
  softCommitmentUrl?: string;
};

type Organization = {
  linkTemplates?: LinkTemplates;
};

type OutletContext = {
  organization: Organization;
};

type ModalRef = {
  openItem: (organization: Organization) => void;
};

const SettingsIntegrationPage = (): JSX.Element => {
  const updateLinkTemplatesModal = useRef<ModalRef>({} as ModalRef);

  const { organization } = useOutletContext<OutletContext>();
  const { getOrganizationId } = useAppContext() as AppContext;

  const shouldRedirect = !isIntegrationEnabled(getOrganizationId());

  if (shouldRedirect) {
    return <Navigate to={PATH_SETTINGS_ORGANIZATION} replace />;
  }

  const style = {
    padding: `${W1}px ${W1 + W0}px`,
    maxWidth: 650,
  };

  const openUpdateLinkTemplates = () =>
    updateLinkTemplatesModal.current.openItem(organization);

  return (
    <div style={style}>
      <OrganizationIntegration
        organization={organization}
        openUpdateLinkTemplates={openUpdateLinkTemplates}
        description={DESCRIPTION_LINK_TEMPLATES}
      />

      <OrganizationUpdateModal
        key="updateLinkTemplates"
        modal={updateLinkTemplatesModal}
        title={LABEL_UPDATE_LINK_TEMPLATES_TITLE}
        organization={organization}
        onlyFieldNames={[
          "linkTemplates",
        ]}
      />
    </div>
  );
};

export default SettingsIntegrationPage;
